import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { Odontogram } from '../components/Odontogram';
import { NotesTimeline } from '../components/NotesTimeline';
import { NutritionTracker } from '../components/NutritionTracker';
import { PatientFilesTab } from '../components/patients/PatientFilesTab';
import { AgendarCitaDialog } from '../components/patients/AgendarCitaDialog';
import { SeguimientoPanel } from '../components/patients/SeguimientoPanel';
import { FichaNutriResumen } from '../components/patients/FichaNutriResumen';
import TratamientoDental from '../components/TratamientoDental';
import { PatientFormDialog } from '../components/patients/PatientFormDialog';
import { EmptyState } from '../components/EmptyState';
import { DENTAL_SPECIALTIES, isDental, isNutricion } from '../lib/specialties';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';

import { API } from '@/lib/api';
import {
  ArrowLeft, Phone, Mail, MapPin, Calendar, AlertTriangle, Plus, Clock, Cake, Pencil,
} from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';


/** Qué módulos enseña la clínica, según las especialidades que ejerce hoy.
 *  Sin doctores todavía (instalación nueva) se muestran todos. */
const modulosDeLaClinica = (especialidades) => {
  const activas = especialidades || [];
  const mods = [];
  if (activas.some(isDental))    mods.push('dental');
  if (activas.some(isNutricion)) mods.push('nutricion');
  return mods.length ? mods : ['dental', 'nutricion'];
};

export const PatientDetail = () => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();

  // Módulos que ofrece la clínica (vienen de /config, según las especialidades
  // de sus doctores activos). null mientras cargan: no se pinta ninguna pestaña.
  const [modulos, setModulos] = useState(null);

  const isDoctor   = user?.rol === 'doctor';
  const esDentista = DENTAL_SPECIALTIES.includes(user?.especialidad);
  // Un doctor ve solo lo de su especialidad; admin y recepción ven todo lo que
  // la clínica ofrezca. Antes el admin caía en la rama dental por descarte.
  const showOdontogram = !!modulos?.includes('dental')    && (!isDoctor || esDentista);
  const showNutricion  = !!modulos?.includes('nutricion') && (!isDoctor || !esDentista);

  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [patientAppointments, setPatientAppointments] = useState([]);
  const [notes, setNotes] = useState([]);
  const [teethData, setTeethData] = useState([]);
  const [archivos, setArchivos] = useState([]);
  const [agendarOpen, setAgendarOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [patientRes, aptsRes, notesRes, archivosRes, configRes] = await Promise.all([
          axios.get(`${API}/patients/${patientId}`),
          axios.get(`${API}/appointments`, { params: { paciente_id: patientId, limit: 100 } }),
          axios.get(`${API}/patients/${patientId}/notas`),
          axios.get(`${API}/patients/${patientId}/archivos`),
          axios.get(`${API}/config`),
        ]);
        const mods = modulosDeLaClinica(configRes.data.especialidades_activas);
        setModulos(mods);
        setPatient(patientRes.data);
        setPatientAppointments(aptsRes.data);
        setNotes(notesRes.data);
        setArchivos(archivosRes.data || []);

        // El odontograma se pide APARTE y solo si toca: ese GET crea el
        // odontograma si no existe, y en una clínica de nutrición ensuciaba
        // el expediente de cada paciente que se abría.
        // try propio: si el odontograma falla NO debe tumbar el expediente ya
        // cargado (el catch de abajo pinta "paciente no encontrado" en un 404).
        if (mods.includes('dental') && (!isDoctor || esDentista)) {
          try {
            const odontogramRes = await axios.get(`${API}/patients/${patientId}/odontogram`);
            setTeethData(odontogramRes.data.dientes || []);
          } catch {
            toast.error('No se pudo cargar el odontograma');
          }
        }
      } catch (err) {
        if (err.response?.status === 404) {
          setPatient(null);
        } else {
          toast.error('Error al cargar datos del paciente');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [patientId, isDoctor, esDentista]);

  const refreshAppointments = async () => {
    try {
      const res = await axios.get(`${API}/appointments`, { params: { paciente_id: patientId, limit: 100 } });
      setPatientAppointments(res.data);
    } catch { /* el toast del diálogo ya informó si falló la creación */ }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[50vh]">
          <p className="text-muted-foreground">Cargando expediente...</p>
        </div>
      </Layout>
    );
  }

  if (!patient) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center min-h-[50vh] text-center">
          <AlertTriangle className="h-12 w-12 text-muted-foreground mb-4" />
          <h2 className="text-xl font-semibold mb-2">Paciente no encontrado</h2>
          <p className="text-muted-foreground mb-4">El paciente solicitado no existe en el sistema.</p>
          <Button onClick={() => navigate('/patients')}>Ver todos los pacientes</Button>
        </div>
      </Layout>
    );
  }

  const handleAddNote = async (noteData) => {
    try {
      const res = await axios.post(`${API}/patients/${patientId}/notas`, {
        paciente_id: patientId,
        contenido: noteData.contenido,
        tags: noteData.tags,
      });
      setNotes([res.data, ...notes]);
      toast.success('Nota clínica agregada');
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(Array.isArray(d) ? d.map(e => e.msg).join(', ') : (d || 'Error al agregar nota'));
    }
  };

  const handleEditNote = async (notaId, noteData) => {
    try {
      const res = await axios.put(`${API}/patients/${patientId}/notas/${notaId}`, {
        contenido: noteData.contenido,
        tags: noteData.tags,
      });
      setNotes(prev => prev.map(n => (n.id === notaId ? res.data : n)));
      toast.success('Nota actualizada');
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(Array.isArray(d) ? d.map(e => e.msg).join(', ') : (d || 'Error al editar la nota'));
    }
  };

  const handleOdontogramUpdate = async (update) => {
    // Optimistic update
    setTeethData(prev => {
      const existing = prev.find(t => t.numero === update.diente_numero);
      if (existing) {
        return prev.map(t =>
          t.numero === update.diente_numero
            ? { ...t, zonas: { ...t.zonas, [update.zona]: update.estado } }
            : t
        );
      }
      return [...prev, { numero: update.diente_numero, zonas: { [update.zona]: update.estado } }];
    });
    try {
      await axios.put(`${API}/patients/${patientId}/odontogram`, update);
      toast.success('Odontograma actualizado');
    } catch (err) {
      toast.error('Error al guardar odontograma');
    }
  };

  const getInitials = (name) =>
    name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'P';

  const parseLocalDate = (dateStr) => {
    if (!dateStr) return new Date(NaN);
    // YYYY-MM-DD sin hora → JS lo parsea como UTC → día incorrecto en UTC-6.
    // Forzar T12:00:00 lo ancla al mediodía local y nunca cruza la medianoche.
    const s = String(dateStr);
    return new Date(s.includes('T') ? s : s + 'T12:00:00');
  };

  const formatDate = (dateStr) =>
    format(parseLocalDate(dateStr), "d 'de' MMMM, yyyy", { locale: es });

  const calculateAge = (birthDate) => {
    const today = new Date();
    const birth = parseLocalDate(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) age--;
    return age;
  };

  // Ordena las citas por fecha y las separa en próximas / anteriores
  const sortedAppointments = [...patientAppointments].sort(
    (a, b) => new Date(b.fecha) - new Date(a.fecha)
  );
  const pastAppointments = sortedAppointments.filter(a => new Date(a.fecha) < new Date());
  const futureAppointments = sortedAppointments.filter(a => new Date(a.fecha) >= new Date());

  return (
    <Layout>
      <div className="space-y-6" data-testid="patient-detail">
        {/* Barra superior: volver + editar */}
        <div className="flex items-center justify-between mb-2">
          <Button variant="ghost" onClick={() => navigate('/patients')} data-testid="back-btn">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Volver
          </Button>
          <Button variant="outline" onClick={() => setEditOpen(true)} data-testid="edit-patient-btn">
            <Pencil className="h-4 w-4 mr-2" />
            Editar paciente
          </Button>
        </div>

        {/* Patient Header */}
        <Card className="bg-card border-border/50">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row gap-6">
              {/* Avatar & Basic Info */}
              <div className="flex items-start gap-4">
                <Avatar className="h-20 w-20 border-4 border-primary/20">
                  <AvatarImage src={patient.avatar_url} />
                  <AvatarFallback className="text-2xl bg-primary/10 text-primary">
                    {getInitials(`${patient.nombre} ${patient.apellido}`)}
                  </AvatarFallback>
                </Avatar>

                <div>
                  <h1 className="text-2xl md:text-3xl font-bold">
                    {patient.nombre} {patient.apellido}
                  </h1>
                  <div className="flex items-center gap-2 mt-1 text-muted-foreground">
                    <Cake className="h-4 w-4" />
                    <span>{formatDate(patient.fecha_nacimiento)} ({calculateAge(patient.fecha_nacimiento)} años)</span>
                  </div>
                </div>
              </div>

              {/* Contact Info */}
              <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3 md:pl-6 md:border-l border-border">
                <div className="flex items-center gap-2 text-sm">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <span>{patient.telefono}</span>
                </div>
                {patient.email && (
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <span>{patient.email}</span>
                  </div>
                )}
                {patient.direccion && (
                  <div className="flex items-center gap-2 text-sm col-span-full">
                    <MapPin className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    <span>{patient.direccion}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Medical Alerts */}
            {patient.alertas_medicas?.length > 0 && (
              <div className="mt-6 p-4 rounded-xl bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-800">
                <div className="flex items-center gap-2 text-rose-600 dark:text-rose-400 mb-2">
                  <AlertTriangle className="h-5 w-5" />
                  <span className="font-semibold">Alertas Médicas</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {patient.alertas_medicas.map((alerta, idx) => (
                    <Badge key={idx} variant="destructive" className="text-sm">
                      {alerta.tipo}: {alerta.descripcion}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Main Content Tabs */}
        <Tabs defaultValue={showOdontogram ? "odontograma" : "nutricion"} className="w-full">
          <div className="overflow-x-auto -mx-1 px-1 pb-1">
            <TabsList className="mb-4 w-max justify-start">
              {showOdontogram && (
                <TabsTrigger value="odontograma" data-testid="tab-odontograma">Odontograma</TabsTrigger>
              )}
              {showOdontogram && (
                <TabsTrigger value="tratamiento" data-testid="tab-tratamiento">Plan de Tratamiento</TabsTrigger>
              )}
              {showNutricion && (
                <TabsTrigger value="nutricion" data-testid="tab-nutricion">Seguimiento Nutricional</TabsTrigger>
              )}
              <TabsTrigger value="notas" data-testid="tab-notas">Notas Clínicas</TabsTrigger>
              <TabsTrigger value="seguimiento" data-testid="tab-seguimiento">Seguimiento</TabsTrigger>
              <TabsTrigger value="citas" data-testid="tab-citas">Historial de Citas</TabsTrigger>
              <TabsTrigger value="archivos" data-testid="tab-archivos">Radiografías y Archivos</TabsTrigger>
            </TabsList>
          </div>

          {/* Odontogram Tab */}
          {showOdontogram && (
            <TabsContent value="odontograma">
              <Odontogram teethData={teethData} onUpdate={handleOdontogramUpdate} />
            </TabsContent>
          )}

          {showOdontogram && (
            <TabsContent value="tratamiento">
              <TratamientoDental patient={patient} />
            </TabsContent>
          )}

          {showNutricion && (
            <TabsContent value="nutricion">
              <div className="space-y-4">
                <FichaNutriResumen patient={patient} />
                <NutritionTracker patient={patient} onEditarPerfil={() => setEditOpen(true)} />
              </div>
            </TabsContent>
          )}

          {/* Clinical Notes Tab */}
          <TabsContent value="notas">
            <NotesTimeline notes={notes} onAddNote={handleAddNote} onEditNote={handleEditNote} />
          </TabsContent>

          {/* Seguimiento (bitácora de contacto, útil sobre todo para prospectos) */}
          <TabsContent value="seguimiento">
            <SeguimientoPanel patient={patient} onCitaAgendada={refreshAppointments} />
          </TabsContent>

          {/* Appointments Tab */}
          <TabsContent value="citas">
            <Card className="bg-card border-border/50">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-semibold">Historial de Citas</CardTitle>
                  <Button size="sm" onClick={() => setAgendarOpen(true)}>
                    <Plus className="h-4 w-4 mr-1" />
                    Agendar cita
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Future Appointments */}
                {futureAppointments.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground mb-3">Próximas Citas</h3>
                    <div className="space-y-3">
                      {futureAppointments.map(apt => (
                        <div key={apt.id} className="flex items-center gap-4 p-3 rounded-lg bg-primary/5 border border-primary/20">
                          <div className="p-2 rounded-lg bg-primary/10">
                            <Calendar className="h-5 w-5 text-primary" />
                          </div>
                          <div className="flex-1">
                            <p className="font-medium">{apt.motivo}</p>
                            <p className="text-sm text-muted-foreground">
                              {formatDate(apt.fecha)} • {apt.hora_inicio} - {apt.hora_fin}
                            </p>
                            <p className="text-sm text-muted-foreground">{apt.doctor_nombre}</p>
                          </div>
                          <Badge variant="outline" className={apt.hora_por_confirmar
                            ? 'bg-amber-100 text-amber-700 border-amber-300'
                            : 'bg-blue-100 text-blue-700 border-blue-300'}>
                            {apt.hora_por_confirmar ? 'Hora por confirmar' : 'Programada'}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Past Appointments */}
                {pastAppointments.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground mb-3">Citas Anteriores</h3>
                    <div className="space-y-3">
                      {pastAppointments.map(apt => (
                        <div key={apt.id} className="flex items-center gap-4 p-3 rounded-lg bg-muted/50">
                          <div className="p-2 rounded-lg bg-muted">
                            <Clock className="h-5 w-5 text-muted-foreground" />
                          </div>
                          <div className="flex-1">
                            <p className="font-medium">{apt.motivo}</p>
                            <p className="text-sm text-muted-foreground">
                              {formatDate(apt.fecha)} • {apt.hora_inicio}
                            </p>
                            <p className="text-sm text-muted-foreground">{apt.doctor_nombre}</p>
                          </div>
                          <Badge variant="outline" className={
                            apt.estado === 'atendido'
                              ? 'bg-emerald-100 text-emerald-700 border-emerald-300'
                              : 'bg-rose-100 text-rose-700 border-rose-300'
                          }>
                            {apt.estado === 'atendido' ? 'Completada' : 'Cancelada'}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {patientAppointments.length === 0 && (
                  <EmptyState
                    icon={Calendar}
                    title="Sin citas registradas"
                    description="Aún no hay citas para este paciente. Agenda una desde el botón de arriba."
                  />
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Files Tab */}
          <TabsContent value="archivos">
            <PatientFilesTab
              patientId={patientId}
              token={token}
              userEmail={user?.email}
              archivos={archivos}
              setArchivos={setArchivos}
              formatDate={formatDate}
            />
          </TabsContent>
        </Tabs>
      </div>

      {/* Agendar cita dialog */}
      <AgendarCitaDialog
        patientId={patientId}
        patient={patient}
        open={agendarOpen}
        onOpenChange={setAgendarOpen}
        onCreated={refreshAppointments}
      />

      {/* Editar paciente dialog */}
      <PatientFormDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        patient={patient}
        onSaved={(updated) => setPatient(updated)}
      />
    </Layout>
  );
};
